import React from 'react';
import { type EdgeProps } from '@xyflow/react';
import { line, curveBasis } from 'd3-shape';
import { ARROW_MARKER_STYLES, LINK_STYLES } from './skillTreeStyles';

/**
 * Smooth S-curve from the source node's bottom anchor to the target node's top anchor.
 * React Flow hands us the handle positions, which sit exactly on those anchors.
 *
 * The path stops tipLength short of the target: the arrowhead marker (anchored at its tail
 * notch) covers that final vertical stretch, so the line never pokes out past the arrow point.
 */
const linkPath = (sourceX: number, sourceY: number, targetX: number, targetY: number): string => {
    const pathEndY = targetY - ARROW_MARKER_STYLES.tipLength;

    // Calculate dynamic offset based on horizontal distance, with maximum of 70
    const baseOffset = 70;
    const horizontalDistance = Math.abs(sourceX - targetX);
    const offset = Math.min(baseOffset, horizontalDistance);

    // Intermediate control points make curveBasis produce a smooth S-curve
    const points: [number, number][] = [
        [sourceX, sourceY],
        [sourceX, sourceY + offset],
        [targetX, pathEndY - offset],
        [targetX, pathEndY],
    ];

    return line<[number, number]>().curve(curveBasis)(points) ?? '';
};

export const SkillEdgeView: React.FC<EdgeProps> = ({ sourceX, sourceY, targetX, targetY }) => {
    const d = linkPath(sourceX, sourceY, targetX, targetY);
    return (
        <g className="link-group" style={{ pointerEvents: 'none' }}>
            {/* Outline path + arrow silhouette behind, gray stroke + gray arrow on top */}
            <path
                className="link-outline"
                d={d}
                stroke={LINK_STYLES.colors.outline}
                strokeWidth={LINK_STYLES.strokeWidths.outline}
                fill="none"
                markerEnd={`url(#${ARROW_MARKER_STYLES.outlineId})`}
            />
            <path
                className="link-stroke"
                d={d}
                stroke={LINK_STYLES.colors.stroke}
                strokeWidth={LINK_STYLES.strokeWidths.stroke}
                fill="none"
                markerEnd={`url(#${ARROW_MARKER_STYLES.id})`}
            />
        </g>
    );
};
